import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { AzureAD, MsalAuthProvider, LoginType, AuthenticationState } from 'react-aad-msal';
import hoistStatics from 'hoist-non-react-statics';
import { apiGet } from '../api';

function scope(tenantName, service, resource, permission) {
    return `https://${tenantName}.onmicrosoft.com/${service}/${resource}.${permission}`;
}

function configureAuthentication(authConfig) {
    const scopes = {
        formFinders: [
            scope(authConfig.tenantName, 'formservice', 'formFinders', 'read'),
            scope(authConfig.tenantName, 'formservice', 'formFinders', 'write'),
        ],
        forms: [
            scope(authConfig.tenantName, 'formservice', 'forms', 'read'),
            scope(authConfig.tenantName, 'formservice', 'forms', 'write'),
        ],
        queues: [
            scope(authConfig.tenantName, 'messagingservice', 'queues', 'read'),
            scope(authConfig.tenantName, 'messagingservice', 'queues', 'write'),
        ],
        licenseTypes: [
            scope(authConfig.tenantName, 'licensingservice', 'licenseTypes', 'read'),
            scope(authConfig.tenantName, 'licensingservice', 'licenseTypes', 'write'),
        ],
        feeTypes: [
            scope(authConfig.tenantName, 'feeservice', 'feeTypes', 'read'),
            scope(authConfig.tenantName, 'feeservice', 'feeTypes', 'write'),
        ],
        workflows: [
            scope(authConfig.tenantName, 'workflowservice', 'workflows', 'read'),
            scope(authConfig.tenantName, 'workflowservice', 'workflows', 'write'),
        ],
        userProfiles: [scope(authConfig.tenantName, 'userprofileservice', 'userProfiles', 'read-self')],
        licenses: [scope(authConfig.tenantName, 'licensingservice', 'licenses', 'read-public')],
        persons: [
            scope(authConfig.tenantName, 'licensingservice', 'persons', 'read-public'),
            scope(authConfig.tenantName, 'licensingservice', 'persons', 'manage-users'),
            scope(authConfig.tenantName, 'licensingservice', 'persons', 'view-users'),
        ],
    };

    const msalConfig = {
        auth: {
            authority: `https://login.microsoftonline.com/${authConfig.tenantId}`,
            clientId: authConfig.clientId,
            redirectUri: `${window.location.origin}/admin`,
        },
    };

    const authenticationParameters = {};

    const options = {
        loginType: LoginType.Redirect,
    };

    return { msalAuthProvider: new MsalAuthProvider(msalConfig, authenticationParameters, options), scopes };
}

const AuthenticationContext = React.createContext();

AuthenticationContext.displayName = 'AuthenticationContext';

export function withAuthenticationContext(Component) {
    const WithAuthenticationCtx = function (props) {
        return (
            <AuthenticationContext.Consumer>
                {(value) => (
                    <Component
                        account={value.account}
                        logout={value.logout}
                        getAccessToken={value.getAccessToken}
                        authorizationScopes={value.authorizationScopes}
                        userProfile={value.userProfile}
                        updateUserProfile={value.updateUserProfile}
                        {...props}
                    />
                )}
            </AuthenticationContext.Consumer>
        );
    };

    WithAuthenticationCtx.displayName = `withAuthenticationContext(${Component.displayName | Component.name})`;

    return hoistStatics(WithAuthenticationCtx, Component);
}

export function useAccount() {
    return useContext(AuthenticationContext).account;
}

export function useLogout() {
    return useContext(AuthenticationContext).logout;
}

export function useAccessTokens() {
    const { getAccessToken, authorizationScopes } = useContext(AuthenticationContext);

    return { getAccessToken, authorizationScopes };
}

export function useUserProfile() {
    return useContext(AuthenticationContext).userProfile;
}

export default function Authenticate(props) {
    const [authProvider, setAuthProvider] = useState();
    const [authScopes, setAuthScopes] = useState();
    const [userProfile, setUserProfile] = useState();

    const getAccessToken = useCallback(
        async (scopes) => (authProvider ? (await authProvider.getAccessToken({ scopes })).accessToken : null),
        [authProvider],
    );

    // MsalAuthProvider may initiate a state change, so call it inside a hook so
    // as not to change the state within the component rendering.
    useEffect(() => {
        if (props.authConfig) {
            const { msalAuthProvider, scopes } = configureAuthentication(props.authConfig);
            setAuthProvider(msalAuthProvider);
            setAuthScopes(scopes);
        } else {
            setAuthProvider(undefined);
            setAuthScopes(undefined);
        }
    }, [props.authConfig]);

    // Extract expression due to compiler comlaint:
    // React Hook useEffect has a complex expression in the dependency array.
    // Extract it to a separate variable so it can be statically checked.
    const authExpression =
        authProvider &&
        authProvider.account &&
        authProvider.account.accountIdentifier &&
        authScopes.userProfiles &&
        getAccessToken;

    useEffect(() => {
        async function getUserProfile() {
            const authToken = await getAccessToken(authScopes.userProfiles);

            const response = await apiGet('userProfiles/personal', authToken);

            setUserProfile(response.data);
        }

        if (authProvider) {
            if (authProvider.account.accountIdentifier) {
                getUserProfile();
            } else {
                setUserProfile(undefined);
            }
        }
        // eslint-disable-next-line
    }, [authExpression]);

    if (!authProvider) {
        return null;
    }

    return (
        <AzureAD provider={authProvider} forceLogin>
            {({ logout, authenticationState, accountInfo }) => {
                if (authenticationState === AuthenticationState.Authenticated) {
                    props.userAuthenticated({
                        account: accountInfo.account,
                        getAccessToken,
                        authorizationScopes: authScopes,
                    });

                    return (
                        <AuthenticationContext.Provider
                            value={{
                                ...accountInfo,
                                logout: customLogout(authProvider, logout),
                                getAccessToken,
                                authorizationScopes: authScopes,
                                userProfile,
                                updateUserProfile: (updatedProfile) => {
                                    setUserProfile(updatedProfile);
                                },
                            }}
                        >
                            {props.children}
                        </AuthenticationContext.Provider>
                    );
                }

                return null;
            }}
        </AzureAD>
    );
}

function customLogout(authProvider, logout) {
    return () => {
        // Using the tenant-specific authority doesn't seem to actually log the user out.
        // Use the common authority instead.
        authProvider.authority = 'https://login.microsoftonline.com/common';

        logout();
    };
}

Authenticate.propTypes = {
    authConfig: PropTypes.object,
};
